// src/pages/ProjectDetailPage.js
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./Project.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

function guessFileType(url) {
  const lower = (url || "").toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".gif")
  ) {
    return "image";
  }
  if (
    lower.endsWith(".txt") ||
    lower.endsWith(".csv") ||
    lower.endsWith(".log")
  ) {
    return "text";
  }
  return "other";
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [bomList, setBomList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bomLoading, setBomLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("info");

  const [fileTextPreview, setFileTextPreview] = useState("");
  const [fileTextLoading, setFileTextLoading] = useState(false);

  const [bomFile, setBomFile] = useState(null);
  const [bomPreview, setBomPreview] = useState(null);
  const [bomApplyLoading, setBomApplyLoading] = useState(false);

  useEffect(() => {
    loadProject();
    loadBomList();
  }, [id]);

  async function loadProject() {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/projects/${id}/`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg = data.detail;
          }
        } catch (e) {}
        throw new Error(msg);
      }

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      setProject(data);
    } catch (e) {
      setError(e.message || "프로젝트 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }

  async function loadBomList() {
    setBomLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/projects/${id}/bom/`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg = data.detail;
          }
        } catch (e) {}
        throw new Error(msg);
      }

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      setBomList(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setBomLoading(false);
    }
  }

  async function loadTextPreview(url) {
    setFileTextLoading(true);
    setFileTextPreview("");
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(url, { 
        cache: "no-store",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const txt = await res.text();
      setFileTextPreview(txt);
    } catch (e) {
      setFileTextPreview("텍스트 파일을 불러오지 못했습니다.");
    } finally {
      setFileTextLoading(false);
    }
  }

  async function handleBomPreviewUpload() {
    if (!bomFile) return;
    const formData = new FormData();
    formData.append("file", bomFile);

    setBomApplyLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${API_BASE}/api/projects/${id}/bom/preview-upload/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData, // FormData일 때는 Content-Type 자동
        }
      );

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg = data.detail;
          }
        } catch (e) {}
        throw new Error(msg);
      }

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      setBomPreview(data);
    } catch (e) {
      alert(e.message || "BOM 미리보기에 실패했습니다.");
    } finally {
      setBomApplyLoading(false);
    }
  }

  function handleBomCandidateChange(rowIndex, productId) {
    if (!bomPreview) return;
    const next = {
      ...bomPreview,
      rows: bomPreview.rows.map((r, i) =>
        i === rowIndex
          ? { ...r, product_id: productId ? Number(productId) : null }
          : r
      ),
    };
    setBomPreview(next);
  }

  async function handleBomApply() {
    if (
      !bomPreview ||
      !Array.isArray(bomPreview.rows) ||
      bomPreview.rows.length === 0
    ) {
      return;
    }
    const rows = bomPreview.rows.map((r) => ({
      bom_index: r.bom_index,
      bom_part: r.bom_part,
      bom_reference: r.bom_reference,
      bom_quantity: r.bom_quantity,
      product_id: r.product_id || null,
    }));

    setBomApplyLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/projects/${id}/bom/apply/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rows }),
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg = data.detail;
          }
        } catch (e) {}
        throw new Error(msg);
      }

      // 성공이면 본문은 안 써도 되니까 굳이 파싱 안 해도 됨
      alert("BOM이 적용되었습니다.");
      setBomPreview(null);
      setBomFile(null);
      await loadBomList();
    } catch (e) {
      alert(e.message || "BOM 적용에 실패했습니다.");
    } finally {
      setBomApplyLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="project-detail-page">
        프로젝트 정보를 불러오는 중...
      </div>
    );
  }

  if (error) {
    return <div className="project-detail-page error">{error}</div>;
  }

  if (!project) {
    return (
      <div className="project-detail-page">프로젝트 정보가 없습니다.</div>
    );
  }

  const files = project.files || {};
  const images = project.images || [];

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <div>
          <h1>{project.name}</h1>
          <div className="project-detail-sub">
            <span>버전: {project.version || "-"}</span>
            <span>생성일: {new Date(project.created_at).toLocaleString()}</span>
            <span>
              업데이트: {new Date(project.updated_at).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="project-detail-stats">
          <div>
            <div className="label">BOM 개수</div>
            <div className="value">{project.bom_count}</div>
          </div>
          <div>
            <div className="label">BOM 합계</div>
            <div className="value">{project.total_bom_cost}</div>
          </div>
        </div>
      </div>

      <div className="project-tabs">
        <button
          className={tab === "info" ? "active" : ""}
          onClick={() => setTab("info")}
        >
          기본 정보
        </button>
        <button
          className={tab === "files" ? "active" : ""}
          onClick={() => setTab("files")}
        >
          파일
        </button>
        <button
          className={tab === "bom" ? "active" : ""}
          onClick={() => setTab("bom")}
        >
          BOM
        </button>
        <button
          className={tab === "images" ? "active" : ""}
          onClick={() => setTab("images")}
        >
          이미지
        </button>
      </div>

      {tab === "info" && (
        <div className="project-info">
          <h2>설명</h2>
          <p>{project.description || "설명이 없습니다."}</p>
        </div>
      )}

      {tab === "files" && (
        <div className="project-files">
          <h2>프로젝트 파일</h2>
          <div className="project-file-list">
            {[
              { key: "manual_file", label: "메뉴얼" },
              { key: "bom_file", label: "BOM 파일" },
              { key: "schematic_pdf", label: "회로도 PDF" },
              { key: "schematic_cad", label: "회로도 ZIP" },
              { key: "artwork_pads", label: "아트웍" },
              { key: "gerber_zip", label: "거버 ZIP" },
            ].map((f) => {
              const url = files[f.key];
              if (!url) return null;
              const type = guessFileType(url);
              return (
                <div key={f.key} className="project-file-item">
                  <div className="project-file-header">
                    <span>{f.label}</span>
                    <a href={url} target="_blank" rel="noreferrer">
                      새창에서 열기
                    </a>
                    {type === "text" && (
                      <button onClick={() => loadTextPreview(url)}>
                        텍스트 미리보기
                      </button>
                    )}
                  </div>
                  {type === "pdf" && (
                    <div className="project-file-preview">
                      <iframe
                        title={f.label}
                        src={url}
                        className="project-file-iframe"
                      />
                    </div>
                  )}
                  {type === "image" && (
                    <div className="project-file-preview">
                      <img
                        src={url}
                        alt={f.label}
                        className="project-file-image"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {fileTextLoading && (
            <div className="project-file-text-loading">
              텍스트 로딩 중...
            </div>
          )}
          {fileTextPreview && (
            <pre className="project-file-text-preview">
              {fileTextPreview}
            </pre>
          )}
        </div>
      )}

      {tab === "bom" && (
        <div className="project-bom">
          <div className="project-bom-header">
            <h2>BOM 리스트</h2>
            <div className="project-bom-upload">
              <input
                type="file"
                accept=".csv,.txt,.xls,.xlsx"
                onChange={(e) =>
                  setBomFile(e.target.files?.[0] || null)
                }
              />
              <button
                disabled={!bomFile || bomApplyLoading}
                onClick={handleBomPreviewUpload}
              >
                BOM 매칭 미리보기
              </button>
            </div>
          </div>

          {bomLoading && <div>BOM을 불러오는 중...</div>}

          {!bomLoading && (
            <table className="project-bom-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>BOM 부품명</th>
                  <th>레퍼런스</th>
                  <th>BOM 수량</th>
                  <th>매칭 제품</th>
                  <th>단가</th>
                  <th>금액</th>
                </tr>
              </thead>
              <tbody>
                {bomList.map((b) => (
                  <tr key={b.id}>
                    <td>{b.bom_index}</td>
                    <td>{b.bom_part}</td>
                    <td>{b.bom_reference}</td>
                    <td>{b.bom_quantity}</td>
                    <td>{b.product_name || "-"}</td>
                    <td>{b.price ?? "-"}</td>
                    <td>{b.line_cost ?? "-"}</td>
                  </tr>
                ))}
                {bomList.length === 0 && (
                  <tr>
                    <td colSpan={7} className="project-empty">
                      등록된 BOM이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {bomPreview && (
            <div className="project-bom-preview">
              <h3>BOM 매칭 미리보기</h3>
              <p>
                80% 이상 유사한 제품 후보를 보여줍니다. 각 행마다 FK로
                연결할 제품을 선택해 주세요.
              </p>
              <table className="project-bom-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>BOM 부품명</th>
                    <th>레퍼런스</th>
                    <th>수량</th>
                    <th>매칭 후보</th>
                  </tr>
                </thead>
                <tbody>
                  {bomPreview.rows.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.bom_index}</td>
                      <td>{r.bom_part}</td>
                      <td>{r.bom_reference}</td>
                      <td>{r.bom_quantity}</td>
                      <td>
                        <select
                          value={r.product_id || ""}
                          onChange={(e) =>
                            handleBomCandidateChange(
                              idx,
                              e.target.value || null
                            )
                          }
                        >
                          <option value="">매칭 안 함</option>
                          {r.candidates.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.similarity}%)
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="project-bom-preview-actions">
                <button
                  disabled={bomApplyLoading}
                  onClick={() => setBomPreview(null)}
                >
                  취소
                </button>
                <button
                  disabled={bomApplyLoading}
                  onClick={handleBomApply}
                >
                  BOM 적용
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "images" && (
        <div className="project-images">
          <h2>프로젝트 이미지</h2>
          <div className="project-image-grid">
            {images.map((img) => (
              <div key={img.id} className="project-image-item">
                <img src={img.image} alt={img.caption} />
                {img.caption && (
                  <div className="caption">{img.caption}</div>
                )}
              </div>
            ))}
            {images.length === 0 && (
              <div className="project-empty">
                등록된 이미지가 없습니다.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
